import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Typography, Snackbar, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import SuperadminLayout from "../../components/layout/SuperadminLayout";
import { Search } from "../../components/Search";
import { Select } from "../../components/Select";
import DataTable from "../../components/DataTable";
import { tenantsService, TenantStatus, Tenant } from "../../services/tenantsService";
import { colors } from "../../theme/colors";
import { useTenantModals } from "./useTenantModals";
import { createTenantColumns, mapTenantToRow, TenantRow } from "./TenantManagementPage.columns";
import {
  TenantManagementPageStyled,
  PageHeader,
  PageTitleBlock,
  FiltersRow,
  PrimaryButton,
} from "./TenantManagementPage.styled";

interface ToastState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info";
}

const TenantManagementPage: React.FC = () => {
  const { t } = useTranslation();

  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TenantStatus | "">("");
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    severity: "success",
  });

  const isFetchingRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const showToast = useCallback(
    (message: string, severity: "success" | "error" | "info" = "success") => {
      setToast({ open: true, message, severity });
    },
    [],
  );

  const fetchTenants = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const response = await tenantsService.getTenants({
        search: debouncedSearch || undefined,
        status: statusFilter !== "" ? statusFilter : null,
      });
      setAllTenants(response.data);
    } catch {
      showToast(t("tenantManagement.errors.loadFailed"), "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [showToast, t, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const getTenantStatusLabel = useCallback(
    (status: TenantStatus): string => {
      switch (status) {
        case TenantStatus.Active:
          return t("tenantManagement.statuses.active");
        case TenantStatus.Invited:
          return t("tenantManagement.statuses.pendingActivation");
        case TenantStatus.Suspended:
          return t("tenantManagement.statuses.suspended");
        case TenantStatus.Inactive:
          return t("tenantManagement.statuses.inactive");
        default:
          return "-";
      }
    },
    [t],
  );

  const filteredRows = useMemo<TenantRow[]>(
    () => allTenants.map((tenant) => mapTenantToRow(tenant, getTenantStatusLabel)),
    [allTenants, getTenantStatusLabel],
  );

  const {
    openAddTenantModal,
    openViewTenantModal,
    openEditTenantModal,
    openActivateModal,
    openSuspendModal,
    openReactivateModal,
    openRestoreModal,
  } = useTenantModals(fetchTenants, showToast);

  const columns = useMemo(
    () =>
      createTenantColumns(
        {
          onViewTenant: openViewTenantModal,
          onEditTenant: openEditTenantModal,
          onActivate: openActivateModal,
          onSuspend: openSuspendModal,
          onReactivate: openReactivateModal,
          onRestore: openRestoreModal,
        },
        t,
      ),
    [
      t,
      openViewTenantModal,
      openEditTenantModal,
      openActivateModal,
      openSuspendModal,
      openReactivateModal,
      openRestoreModal,
    ],
  );

  const statusOptions = useMemo(
    () => [
      { value: "", label: t("tenantManagement.filters.all") },
      { value: TenantStatus.Active, label: t("tenantManagement.statuses.active") },
      { value: TenantStatus.Invited, label: t("tenantManagement.statuses.pendingActivation") },
      { value: TenantStatus.Suspended, label: t("tenantManagement.statuses.suspended") },
    ],
    [t],
  );

  return (
    <SuperadminLayout>
      <TenantManagementPageStyled>
        <PageHeader>
          <PageTitleBlock>
            <Typography variant="h1">{t("tenantManagement.title")}</Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {t("tenantManagement.subtitle")}
            </Typography>
          </PageTitleBlock>

          <PrimaryButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddTenantModal}
          >
            {t("tenantManagement.addNewTenant")}
          </PrimaryButton>
        </PageHeader>

        <FiltersRow>
          <Search
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("tenantManagement.searchPlaceholder")}
            width={380}
          />
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as TenantStatus | "")}
            options={statusOptions}
            label={t("tenantManagement.filters.all")}
            width={200}
          />
        </FiltersRow>

        <DataTable<TenantRow>
          columns={columns}
          data={filteredRows}
          loading={loading}
          emptyMessage={t("tenantManagement.noTenantsFound")}
        />
      </TenantManagementPageStyled>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </SuperadminLayout>
  );
};

export default TenantManagementPage;
